// Раздел «Деньги»: касса регионов и ФОТ. Видит его один человек — владелец.
//
// ЧЕГО ЭТОТ ЭКРАН НЕ ДЕЛАЕТ. Не решает, кому он доступен. Ограничение стоит на сервере,
// в двух независимых местах: роль в BFF и проверка владельца в модуле ядра. Здесь только
// честный ответ на 403 — «раздел вам не открыт», без попытки что-то дорисовать.
//
// ЧЕТЫРЕ ВЕЩИ, БЕЗ КОТОРЫХ ЭКРАН БЫЛ БЫ ВРАНЬЁМ, И ОНИ ЗДЕСЬ ВИДНЫ ВСЕГДА:
//   1) сколько операций отсечено как мусор разбора и по какому правилу — иначе касса
//      Владивостока показывает миллиарды (28 операций с расходом больше 100 млн ₽);
//   2) месяц отчётный, а не календарный — с бухгалтерией не совпадает;
//   3) разрез по людям НЕПОЛОН: у части выплат получатель остался внутри комментария;
//   4) это деньги вне расчётного счёта — с 1С и банком напрямую не сверяются.
// Все четыре — постоянные строки, а не подсказки при наведении: человек, который придёт
// сверять эти цифры с бухгалтерией, должен узнать о расхождении до того, как начнёт
// искать ошибку.

import { useState } from 'react'

import {
  НеВладелец, useДвижение, useДеньгиСправочники, useКассу, useОстатки, useФОТ,
  useФОТПоЛюдям,
} from '../api/money'
import type { Отсечение, СтрокаАгрегата } from '../api/money'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, fmtText } from '../lib/format'
import {
  ПОДПИСЬ_РАЗРЕЗА, ПРО_НАЛИЧНЫЕ, ПРО_ОТЧЁТНЫЙ_МЕСЯЦ, ПРО_ФОТ, доля, значениеРазреза,
  ключЧеловека, классСходимости, месяцЧеловеку, отсечениеЗаметно, полнотаПоЛюдям,
  правилоОтсечения, подписьОтсечения, проНесведённыеРегионы, разборОстатка, склонение,
  строкаЗапроса,
} from '../lib/money_report'

const СТРАНИЦА = 50

type Вкладка = 'cash' | 'fot' | 'people' | 'balances' | 'ops' | 'junk'

const ВКЛАДКИ: { код: Вкладка; название: string; смысл: string }[] = [
  { код: 'cash', название: 'Касса', смысл: 'Приход и расход по регионам и отчётным месяцам' },
  { код: 'fot', название: 'ФОТ по регионам', смысл: 'Выплаты людям: суммы без имён' },
  { код: 'people', название: 'ФОТ по людям', смысл: 'Кому и сколько выплачено наличными' },
  { код: 'balances', название: 'Остатки', смысл: 'Касса на начало и конец отчётного месяца' },
  { код: 'ops', название: 'Движение', смысл: 'Реестр операций и крупнейшие выплаты' },
  { код: 'junk', название: 'Отсечённое', смысл: 'Ровно те строки, которые отчёты не считают' },
]

// ─────────────────────────── общие куски ───────────────────────────

/**
 * Постоянная шапка оговорок. Не сворачивается: три факта ниже меняют смысл каждой цифры
 * на экране, и прятать их за «подробнее» значило бы показывать цифры без них.
 */
function Оговорки({ несведённые }: { несведённые?: string[] }) {
  const регионы = проНесведённыеРегионы(несведённые)
  return (
    <div className="notice" style={{ marginBottom: 16 }}>
      <div>{ПРО_ОТЧЁТНЫЙ_МЕСЯЦ}</div>
      <div style={{ marginTop: 8 }}>{ПРО_НАЛИЧНЫЕ}</div>
      <div style={{ marginTop: 8 }}>{ПРО_ФОТ}</div>
      {регионы && <div style={{ marginTop: 8 }}>{регионы}</div>}
    </div>
  )
}

/**
 * Строка про отсечение — рядом с суммами, а не внизу страницы.
 * Когда отсечённое заметно на фоне выборки, поднимаем её в `notice`: 4 % строк дают
 * в кассе Владивостока порядок величины, и мелкий серый текст такое не удержит.
 */
function ПолосаОтсечения({ cut, расход }: { cut?: Отсечение | null; расход: number }) {
  const заметно = отсечениеЗаметно(cut, расход)
  return (
    <div className={заметно ? 'notice' : 'hint'} style={{ marginBottom: 12 }}>
      <div>{подписьОтсечения(cut)}</div>
      <div className="muted" style={{ marginTop: 4 }}>{правилоОтсечения(cut)}</div>
    </div>
  )
}

function Плитки({ строки }: { строки: { подпись: string; значение: string; горячо?: boolean }[] }) {
  return (
    <div className="tiles" style={{ marginBottom: 16 }}>
      {строки.map((т) => (
        <div className={т.горячо ? 'tile hot' : 'tile'} key={т.подпись}>
          <div className="t-label">{т.подпись}</div>
          <div className="t-sum">{т.значение}</div>
        </div>
      ))}
    </div>
  )
}

function Пагинация({
  start, total, next, занято, назад, вперёд,
}: {
  start: number; total: number; next: number | null; занято: boolean
  назад: () => void; вперёд: () => void
}) {
  if (total <= СТРАНИЦА) return null
  return (
    <div className="pager">
      <button disabled={start === 0 || занято} onClick={назад}>← назад</button>
      <span className="muted">
        {start + 1}–{Math.min(start + СТРАНИЦА, total)} из {total}
      </span>
      <button disabled={next === null || занято} onClick={вперёд}>вперёд →</button>
    </div>
  )
}

/** Полоска доли в ячейке. Ширина — доля от итога; ноль и NaN дают пустую ячейку. */
function Полоска({ часть }: { часть: number }) {
  return (
    <div aria-hidden="true"
         style={{ height: 4, borderRadius: 2, background: 'var(--border)', marginTop: 4 }}>
      <div style={{ width: `${(часть * 100).toFixed(1)}%`, height: '100%', borderRadius: 2,
                    background: 'var(--accent)' }} />
    </div>
  )
}

/** Пусто — но почему. «Ничего не найдено» в кассе читается как «выплат не было». */
function Пусто({ что }: { что: string }) {
  return (
    <div className="empty">
      <div className="empty-title">Ни одной операции по этим условиям</div>
      <div className="empty-hint">
        {что} Это не значит «денег не было»: проверьте регион и отчётный месяц — месяц здесь
        отчётный, и операция могла попасть в соседний.
      </div>
    </div>
  )
}

/**
 * Обёртка над QueryState: 403 здесь не поломка, а нормальный ответ сервера, и выглядеть
 * он должен как объяснение, а не как «Ошибка: forbidden».
 */
function Состояние({
  isLoading, error, children,
}: { isLoading: boolean; error: unknown; children: React.ReactNode }) {
  if (error instanceof НеВладелец) {
    return (
      <div className="card">
        <div className="empty-title">Раздел не открыт вашей учётке</div>
        <div className="empty-hint">
          Касса регионов и фонд оплаты труда видны только владельцу. Это решение владельца,
          а не сбой: до сегодняшнего дня эти данные были закрыты вообще всем, включая ядро.
        </div>
      </div>
    )
  }
  return <QueryState isLoading={isLoading} error={error}>{children}</QueryState>
}

// ─────────────────────────── касса и ФОТ: общая таблица ───────────────────────────

function ТаблицаАгрегата({
  строки, разрезы, итогРасхода, итогПрихода,
}: {
  строки: СтрокаАгрегата[]; разрезы: string[]; итогРасхода: number; итогПрихода: number
}) {
  if (!строки.length) return <Пусто что="Ядро вернуло пустой список." />
  // Подпись разреза («Регион», «Кому») считается один раз и идёт и в шапку, и в data-label:
  // на телефоне таблица разбирается на карточки (styles.css, §12), шапки там нет. Разрезы
  // приходят списком кодов, поэтому вторая копия выражения молча разъехалась бы с первой.
  const подпись = (к: string) => ПОДПИСЬ_РАЗРЕЗА[к] ?? к
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            {разрезы.map((к) => <th key={к}>{подпись(к)}</th>)}
            <th className="num nowrap">Расход</th>
            <th className="num nowrap">Приход</th>
            <th className="num nowrap">Операций</th>
            {/* Сколько операций с заполненным получателем: по нему видно, можно ли верить
                разрезу «по людям» именно в этом регионе. */}
            <th className="num nowrap">с получателем</th>
            <th className="nowrap">Период операций</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r, i) => (
            <tr key={разрезы.map((к) => значениеРазреза(r, к)).join('|') + i}>
              {разрезы.map((к) => (
                <td key={к} data-label={подпись(к)}>
                  {значениеРазреза(r, к)}
                  {к === разрезы[0] && итогРасхода > 0 && (
                    <Полоска часть={доля(r.spent, итогРасхода)} />
                  )}
                </td>
              ))}
              <td className="num nowrap" data-label="Расход">{fmtMoney(r.spent)}</td>
              <td className="num nowrap" data-label="Приход">{fmtMoney(r.received)}</td>
              <td className="num nowrap" data-label="Операций">{r.ops}</td>
              <td className="num nowrap muted" data-label="с получателем">
                {r.named}
                {r.ops > 0 && r.named < r.ops && (
                  <span className="faint"> / {r.ops}</span>
                )}
              </td>
              <td className="nowrap muted" data-label="Период операций">
                {r.first_day ? `${fmtDate(r.first_day)} — ${fmtDate(r.last_day)}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={разрезы.length}><b>Итого по всей выборке</b></td>
            <td className="num nowrap" data-label="Расход"><b>{fmtMoney(итогРасхода)}</b></td>
            <td className="num nowrap" data-label="Приход"><b>{fmtMoney(итогПрихода)}</b></td>
            {/* Колонки, по которым итог не считается. В карточке пустая ячейка не
                показывается вовсе, поэтому подпись у неё пустая, а не забытая. */}
            <td colSpan={2} />
            <td data-label="" />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ─────────────────────────── вкладка: касса / ФОТ по регионам ───────────────────────────

function ВкладкаАгрегата({
  запрос, фот, разрез, приРазрезе,
}: {
  запрос: ReturnType<typeof useКассу>; фот: boolean
  разрез: string; приРазрезе: (v: string) => void
}) {
  const d = запрос.data
  const итог = d?.total_row
  return (
    <Состояние isLoading={запрос.isLoading} error={запрос.error}>
      {d && (
        <>
          <Плитки строки={[
            { подпись: фот ? 'Выплачено людям' : 'Израсходовано', значение: fmtMoney(итог?.spent ?? 0) },
            { подпись: фот ? 'Приход (не выплаты)' : 'Получено', значение: fmtMoney(итог?.received ?? 0) },
            { подпись: 'Операций', значение: String(итог?.ops ?? 0) },
            { подпись: 'Регионов', значение: String(итог?.regions ?? 0) },
            {
              подпись: 'Отчётные месяцы',
              значение: итог?.ym_first
                ? `${месяцЧеловеку(итог.ym_first)} — ${месяцЧеловеку(итог.ym_last)}`
                : '—',
            },
            {
              подпись: 'Отсечено как мусор',
              значение: `${d.cut.ops} ${склонение(d.cut.ops, 'операция', 'операции', 'операций')}`,
              горячо: отсечениеЗаметно(d.cut, итог?.spent ?? 0),
            },
          ]} />

          <ПолосаОтсечения cut={d.cut} расход={итог?.spent ?? 0} />

          {фот && (
            <div className="hint" style={{ marginBottom: 12 }}>
              Считаются только категории выплат людям: {d.payout_categories.join(', ')}.
              «Под сделку» и «Офис» — тоже расход кассы, но это не зарплата, и сюда они
              не входят.
            </div>
          )}

          <div className="card">
            <FilterBar id="money-cash-cut" style={{ marginBottom: 12 }}>
              <div className="f">
                <label htmlFor="mrz">Разрез</label>
                <select id="mrz" value={разрез} onChange={(e) => приРазрезе(e.target.value)}>
                  <option value="region">по регионам</option>
                  <option value="ym">по отчётным месяцам</option>
                  <option value="region+ym">по регионам и месяцам</option>
                  <option value="category">по категориям</option>
                  <option value="card">по кассам и каналам</option>
                  <option value="direction">по направлениям</option>
                </select>
              </div>
            </FilterBar>
            <ТаблицаАгрегата
              строки={d.rows} разрезы={d.by}
              итогРасхода={итог?.spent ?? 0} итогПрихода={итог?.received ?? 0}
            />
          </div>
        </>
      )}
    </Состояние>
  )
}

// ─────────────────────────── вкладка: ФОТ по людям ───────────────────────────

function ВкладкаЛюди({ путь }: { путь: string }) {
  const [start, setStart] = useState(0)
  const q = useФОТПоЛюдям(путь + (путь.includes('?') ? '&' : '?') + `limit=${СТРАНИЦА}&start=${start}`)
  const d = q.data
  const полнота = полнотаПоЛюдям(d?.total_row)

  return (
    <Состояние isLoading={q.isLoading} error={q.error}>
      {d && (
        <>
          <Плитки строки={[
            { подпись: 'Выплачено людям', значение: fmtMoney(d.total_row?.spent ?? 0) },
            { подпись: 'Разных получателей', значение: String(полнота.людей) },
            { подпись: 'Выплат', значение: String(d.total_row?.ops ?? 0) },
            {
              подпись: 'Без указанного получателя',
              значение: fmtMoney(d.total_row?.unnamed_spent ?? 0),
              горячо: полнота.доляБезИмени > 0.1,
            },
          ]} />

          {/* Главная оговорка этой вкладки — над таблицей, а не под ней: список по людям
              неполон по построению, и человек должен узнать это до того, как поверит
              строке «этому выплачено столько-то». */}
          <div className="notice" style={{ marginBottom: 12 }}>{полнота.текст}</div>
          <ПолосаОтсечения cut={d.cut} расход={d.total_row?.spent ?? 0} />

          <div className="card">
            {d.rows.length === 0 ? (
              <Пусто что="Ни одной выплаты с такими условиями ядро не вернуло." />
            ) : (
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th>Получатель</th>
                      <th className="num nowrap">Выплачено</th>
                      <th className="num nowrap">Выплат</th>
                      <th className="nowrap">Отчётные месяцы</th>
                      <th>Регионы</th>
                      <th>Категории</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r, i) => (
                      <tr key={ключЧеловека(r, start + i)}>
                        <td data-label="Получатель">
                          {r.named ? fmtText(r.person) : (
                            <span className="muted">
                              получатель не указан
                              <span className="faint"> — фамилия внутри комментария</span>
                            </span>
                          )}
                        </td>
                        <td className="num nowrap" data-label="Выплачено">{fmtMoney(r.spent)}</td>
                        <td className="num nowrap" data-label="Выплат">{r.ops}</td>
                        <td className="nowrap muted" data-label="Отчётные месяцы">
                          {r.ym_first === r.ym_last
                            ? месяцЧеловеку(r.ym_first)
                            : `${месяцЧеловеку(r.ym_first)} — ${месяцЧеловеку(r.ym_last)}`}
                        </td>
                        <td className="muted" data-label="Регионы">{r.regions.join(', ')}</td>
                        <td className="muted" data-label="Категории">{r.categories.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Пагинация
              start={d.start} total={d.total} next={d.next} занято={q.isFetching}
              назад={() => setStart(Math.max(0, start - СТРАНИЦА))}
              вперёд={() => setStart(d.next ?? start)}
            />
          </div>
        </>
      )}
    </Состояние>
  )
}

// ─────────────────────────── вкладка: остатки ───────────────────────────

function ВкладкаОстатки({ путь }: { путь: string }) {
  const [start, setStart] = useState(0)
  const q = useОстатки(путь + (путь.includes('?') ? '&' : '?') + `limit=${СТРАНИЦА}&start=${start}`)
  const d = q.data
  const разборы = (d?.rows ?? []).map(разборОстатка)
  const расходится = разборы.filter((р) => р.вид === 'расходится').length
  const нечем = разборы.filter((р) => р.вид === 'не с чем').length

  return (
    <Состояние isLoading={q.isLoading} error={q.error}>
      {d && (
        <>
          <Плитки строки={[
            { подпись: 'Строк остатков на странице', значение: String(d.rows.length) },
            { подпись: 'Не сходятся с реестром', значение: String(расходится), горячо: расходится > 0 },
            { подпись: 'Сверить не с чем', значение: String(нечем) },
          ]} />

          <div className="hint" style={{ marginBottom: 12 }}>
            Остаток нужен ровно для одного: поймать ошибку разбора xlsx. «Начало + приход −
            расход» должно давать «конец». Расчёт ведётся по тем же отсечённым данным, что и
            остальные экраны раздела, — иначе цифры не сошлись бы между вкладками.
          </div>
          <ПолосаОтсечения cut={d.cut} расход={0} />

          <div className="card">
            {d.rows.length === 0 ? (
              <Пусто что="Остатков по этим условиям в зеркале нет." />
            ) : (
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th>Регион</th>
                      <th className="nowrap">Отчётный месяц</th>
                      <th className="nowrap">Вид</th>
                      <th className="num nowrap">На начало</th>
                      <th className="num nowrap">Приход</th>
                      <th className="num nowrap">Расход</th>
                      <th className="num nowrap">По расчёту</th>
                      <th className="num nowrap">На конец (источник)</th>
                      <th>Сходимость</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r, i) => {
                      const разбор = разборы[i]
                      return (
                        <tr key={r.bal_key}>
                          <td data-label="Регион">{fmtText(r.region)}</td>
                          <td className="nowrap" data-label="Отчётный месяц">{месяцЧеловеку(r.ym)}</td>
                          <td className="nowrap muted" data-label="Вид">{fmtText(r.kind)}</td>
                          <td className="num nowrap" data-label="На начало">{fmtMoney(r.opening)}</td>
                          <td className="num nowrap" data-label="Приход">{fmtMoney(r.received)}</td>
                          <td className="num nowrap" data-label="Расход">{fmtMoney(r.spent)}</td>
                          <td className="num nowrap" data-label="По расчёту">{fmtMoney(r.calc_closing)}</td>
                          <td className="num nowrap" data-label="На конец (источник)">
                            {r.closing === null ? <span className="muted">не заполнен</span>
                              : fmtMoney(r.closing)}
                          </td>
                          <td data-label="Сходимость">
                            <span className={классСходимости(разбор.вид)}>{разбор.вид}</span>
                            <div className="faint" style={{ marginTop: 4 }}>{разбор.текст}</div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Пагинация
              start={d.start} total={d.total} next={d.next} занято={q.isFetching}
              назад={() => setStart(Math.max(0, start - СТРАНИЦА))}
              вперёд={() => setStart(d.next ?? start)}
            />
          </div>
        </>
      )}
    </Состояние>
  )
}

// ─────────────────────────── вкладка: движение и отсечённое ───────────────────────────

function ВкладкаДвижение({ путь, мусор }: { путь: string; мусор: boolean }) {
  const [start, setStart] = useState(0)
  const [сорт, setСорт] = useState<'date' | 'spent' | 'received'>(мусор ? 'spent' : 'date')
  const хвост = (путь.includes('?') ? '&' : '?')
    + `sort=${сорт}&limit=${СТРАНИЦА}&start=${start}` + (мусор ? '&junk=1' : '')
  const q = useДвижение(путь + хвост)
  const d = q.data

  return (
    <Состояние isLoading={q.isLoading} error={q.error}>
      {d && (
        <>
          <Плитки строки={[
            { подпись: мусор ? 'Отсечено операций' : 'Операций', значение: String(d.total_row?.ops ?? 0), горячо: мусор },
            { подпись: 'Израсходовано', значение: fmtMoney(d.total_row?.spent ?? 0) },
            { подпись: 'Получено', значение: fmtMoney(d.total_row?.received ?? 0) },
          ]} />

          {мусор ? (
            // Экран «покажи, что именно отсекли». Без него «отсечено 666» — обещание,
            // а не факт: проверить его иначе нечем.
            <div className="notice" style={{ marginBottom: 12 }}>
              Здесь ровно те строки, которые остальные вкладки НЕ считают. Их суммы
              недостоверны: колонки в xlsx съехали при разборе. Чинить это надо в самой
              exec-панели — зеркало и CRM источник не правят. {правилоОтсечения(d.cut)}
            </div>
          ) : (
            <>
              <ПолосаОтсечения cut={d.cut} расход={d.total_row?.spent ?? 0} />
              <div className="hint" style={{ marginBottom: 12 }}>
                Крупнейшие выплаты — это сортировка «по расходу»: выберите её, чтобы увидеть
                самые большие операции первыми.
              </div>
            </>
          )}

          <div className="card">
            <FilterBar id="money-ops-sort" style={{ marginBottom: 12 }}>
              <div className="f">
                <label htmlFor="msort">Сортировка</label>
                <select id="msort" value={сорт}
                        onChange={(e) => { setСорт(e.target.value as 'date'); setStart(0) }}>
                  <option value="date">по дате операции</option>
                  <option value="spent">по расходу (крупнейшие выплаты)</option>
                  <option value="received">по приходу</option>
                </select>
              </div>
            </FilterBar>

            {d.rows.length === 0 ? (
              <Пусто что={мусор
                ? 'Мусора разбора по этим условиям не нашлось — и это хорошая новость.'
                : 'Ядро вернуло пустой список.'} />
            ) : (
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>
                      <th className="nowrap">Дата</th>
                      <th>Регион</th>
                      <th className="nowrap">Отчётный месяц</th>
                      <th>Категория</th>
                      <th>Получатель</th>
                      <th className="num nowrap">Расход</th>
                      <th className="num nowrap">Приход</th>
                      <th>Комментарий</th>
                      <th className="nowrap">Касса / канал</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r) => (
                      <tr key={r.src_id}>
                        <td className="nowrap" data-label="Дата">
                          {r.op_date ? fmtDate(r.op_date) : (
                            // У 5 операций дата в источнике с опечаткой. Показываем сырое
                            // значение: «—» скрыло бы, что дата ЕСТЬ, просто нечитаемая.
                            <span className="muted" title="дата в источнике не разбирается">
                              {fmtText(r.op_date_raw)}
                            </span>
                          )}
                        </td>
                        <td data-label="Регион">{fmtText(r.region)}</td>
                        <td className="nowrap muted" data-label="Отчётный месяц">{месяцЧеловеку(r.ym)}</td>
                        <td data-label="Категория">{fmtText(r.category)}</td>
                        <td data-label="Получатель">{fmtText(r.fio)}</td>
                        <td className="num nowrap" data-label="Расход">{fmtMoney(r.spent)}</td>
                        <td className="num nowrap" data-label="Приход">{fmtMoney(r.received)}</td>
                        <td className="muted" data-label="Комментарий">{fmtText(r.comment)}</td>
                        <td className="nowrap muted" data-label="Касса / канал">{fmtText(r.card)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Пагинация
              start={d.start} total={d.total} next={d.next} занято={q.isFetching}
              назад={() => setStart(Math.max(0, start - СТРАНИЦА))}
              вперёд={() => setStart(d.next ?? start)}
            />
          </div>
        </>
      )}
    </Состояние>
  )
}

// ─────────────────────────── экран целиком ───────────────────────────

export default function Money() {
  const [вкладка, setВкладка] = useState<Вкладка>('cash')
  const [регион, setРегион] = useState('')
  const [сНачала, setСНачала] = useState('')
  const [поКонец, setПоКонец] = useState('')
  const [разрез, setРазрез] = useState('region')
  const [поиск, setПоиск] = useState('')

  const refs = useДеньгиСправочники()
  const путь = строкаЗапроса({
    region: регион || undefined,
    ym_from: сНачала || undefined,
    ym_to: поКонец || undefined,
    q: поиск.trim() || undefined,
  })

  // Агрегаты грузим только на своей вкладке: лишний запрос к кассе — это лишняя строка
  // в журнале доступа к зарплатам, а журнал должен показывать, что человек СМОТРЕЛ.
  const срез = путь + (путь ? '&' : '?') + `by=${разрез}`
  const касса = useКассу(срез, вкладка === 'cash')
  const фот = useФОТ(срез, вкладка === 'fot')

  const месяцы = refs.data?.months ?? []
  const регионы = refs.data?.regions_in_data ?? []
  const счётчики = refs.data?.counts

  return (
    <>
      <div className="page-head">
        <h1>Касса и ФОТ</h1>
        <span className="badge">видно только владельцу</span>
        <div className="spacer" />
        {счётчики && (
          <span className="muted">
            {счётчики.ops_all}{' '}
            {склонение(счётчики.ops_all, 'операция', 'операции', 'операций')} кассы,
            из них {счётчики.ops_junk} — мусор разбора
          </span>
        )}
      </div>

      <Оговорки несведённые={refs.data?.regions_unmapped} />

      {/* Пустые таблицы панели — факт, который надо назвать словами один раз наверху,
          иначе человек будет искать «настоящий ФОТ» в разделе, которого нет.
          КАЖДАЯ ТАБЛИЦА ГОВОРИТ ЗА СЕБЯ. Раньше обе подписи стояли под одним условием
          `fot_rows === 0`: наполнись таблица ФОТ — и подпись про пустой ручной ввод
          расходов исчезла бы вместе с ней, хотя он остался бы пуст. Такая пропажа
          незаметна и потому опасна: пустой раздел снова читался бы как поломка. */}
      {счётчики && (счётчики.fot_rows === 0 || счётчики.cash_expense_rows === 0) && (
        <div className="hint" style={{ marginBottom: 16 }}>
          {счётчики.fot_rows === 0 && 'Таблица ФОТ в exec-панели пуста (0 строк). '}
          {счётчики.cash_expense_rows === 0
            && 'Ручной ввод расходов подразделений в панели тоже пуст (0 строк). '}
          Это не сбой чтения и не отставание синка: в источнике таких записей нет вовсе.
          Всё, что показано ниже, посчитано по реестру кассы регионов.
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="money">
          <div className="f">
            <label htmlFor="mreg">Регион</label>
            <select id="mreg" value={регион} onChange={(e) => setРегион(e.target.value)}>
              <option value="">все регионы</option>
              {регионы.map((r) => (
                <option key={r.k} value={r.k}>{r.k} ({r.n})</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="mfrom">Отчётный месяц с</label>
            <select id="mfrom" value={сНачала} onChange={(e) => setСНачала(e.target.value)}>
              <option value="">с самого начала</option>
              {месяцы.filter((m) => /^\d{4}-\d{2}$/.test(m)).map((m) => (
                <option key={m} value={m}>{месяцЧеловеку(m)}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="mto">по</label>
            <select id="mto" value={поКонец} onChange={(e) => setПоКонец(e.target.value)}>
              <option value="">по самый свежий</option>
              {месяцы.filter((m) => /^\d{4}-\d{2}$/.test(m)).map((m) => (
                <option key={m} value={m}>{месяцЧеловеку(m)}</option>
              ))}
            </select>
          </div>
          <div className="f" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="mq">Поиск по получателю, комментарию, сделке</label>
            <input id="mq" value={поиск} onChange={(e) => setПоиск(e.target.value)}
                   placeholder="фамилия или слово из комментария" />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          Поиск идёт и по комментарию: у части операций получатель записан только там.
        </div>
      </div>

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» ставит кнопки в столбец, и шесть вкладок съедают
          весь первый экран до единственного, ради чего его открыли. Нажатая — aria-pressed,
          как на остальных экранах с вкладками. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {ВКЛАДКИ.map((в) => (
          <button key={в.код} title={в.смысл}
                  aria-pressed={в.код === вкладка}
                  onClick={() => setВкладка(в.код)}>
            {в.название}
          </button>
        ))}
      </div>

      {вкладка === 'cash' && (
        <ВкладкаАгрегата запрос={касса} фот={false} разрез={разрез} приРазрезе={setРазрез} />
      )}
      {вкладка === 'fot' && (
        <ВкладкаАгрегата запрос={фот} фот разрез={разрез} приРазрезе={setРазрез} />
      )}
      {вкладка === 'people' && <ВкладкаЛюди путь={путь} />}
      {вкладка === 'balances' && <ВкладкаОстатки путь={путь} />}
      {вкладка === 'ops' && <ВкладкаДвижение путь={путь} мусор={false} />}
      {вкладка === 'junk' && <ВкладкаДвижение путь={путь} мусор />}

      <div className="hint" style={{ marginTop: 16 }}>
        Каждое открытие любой из этих вкладок записывается в журнал доступа: это зарплаты,
        и должно быть видно, кто и когда их смотрел. Строка запроса в журнал не пишется —
        иначе журнал сам стал бы выгрузкой фамилий.
      </div>
    </>
  )
}
